import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import { fetchBrands } from "@/lib/api";
import { ChevronRight, ArrowUpRight } from "lucide-react";

const PILLARS = [
  { n: "01", t: "Brand Craftsmanship", b: "Each house is held to its own register — typography, palette, voice. We do not normalise. We preserve." },
  { n: "02", t: "Cultural Richness", b: "From the DACH heritage of Castlewood to the dark luxury of Brionj, every brand pays attention to place." },
  { n: "03", t: "Intentional Hospitality", b: "Bookings at scale; enquiries by hand. We meet each guest where the visit asks us to." },
];

const PORTFOLIO = [
  {
    slug: "castlewood",
    name: "Castlewood",
    italicName: "Castlewood",
    label: "Heritage Brand",
    description: "Heritage country hotels in Germany, Austria & Switzerland.",
    stats: ["8 properties", "DACH region", "Heritage", "Since 2017"],
    href: "/castlewood",
    // Schlosshotel Althörnitz — the signature Castlewood image (autumnal
    // Central European castle on a wooded hill), matched to the Castlewood
    // homepage hero and to the Althörnitz property page so the parent
    // GMH page reads as the same brand.
    image: "https://images.unsplash.com/photo-1759675274095-e19cc2e12055?w=2000&q=85&auto=format",
    cta: "Explore Castlewood",
  },
  {
    slug: "vela",
    name: "Vela",
    italicName: "Vela",
    label: "Resort Brand",
    description: "The other home. Coastal hotels along Germany's Baltic coast.",
    stats: ["5 hotels", "Baltic coast", "Since 2019"],
    href: "/vela",
    // The Breeze (Heiligenhafen) — a long wooden pier extending into a bright
    // pale-blue Baltic on a clear day with a distant coastline visible. The
    // same image is used as one of The Breeze's gallery shots, keeping the
    // Vela brand identity consistent from the parent page through to the
    // property detail.
    image: "https://images.unsplash.com/photo-1769293076973-f298b9eac9f9?w=2000&q=85&auto=format",
    cta: "Explore Vela",
  },
  {
    slug: "brionj",
    name: "Brionj",
    italicName: "Brionj",
    label: "Private Residences",
    description: "A small collection of private residences — held by enquiry.",
    stats: ["4 residences", "Worldwide", "By enquiry"],
    href: "/brionj",
    // A classical neoclassical lakeside villa on Lake Como, illuminated from
    // within at dusk — the iconic image of European ultra-luxury private
    // residences. Warm window light glows against the dark wooded hillside;
    // the lake reflects in the foreground; distant villa lights dot the
    // shore above. Editorially restrained, atmospheric, not bright — exactly
    // the Brionj "held by enquiry" register.
    image: "https://images.unsplash.com/photo-1762862170883-4cfc75c7672a?w=2000&q=85&auto=format",
    cta: "Discover Brionj",
  },
];

const GMHHome = () => {
  const [brands, setBrands] = useState([]);
  useEffect(() => { fetchBrands().then(setBrands).catch(() => {}); }, []);

  return (
    <div data-testid="gmh-home">
      {/* ---------------- HERO (Navy editorial with photographic depth) ---------------- */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground" data-testid="gmh-hero">
        {/* Editorial photographic background: an iconic Belle Époque Swiss
            luxury hotel — Park Hotel Vitznau on Lake Lucerne — turrets,
            spires, mansard roof set against verdant alpine hills with the
            lake in the foreground. Reads unmistakably as ultra-luxury
            European heritage hospitality. User-selected. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1760454220991-ca0577d5bd6a?w=2400&q=85&auto=format')",
          }}
        />
        {/* Navy wash: ~38% of the institutional navy (#1F3A5C). Lighter than
            previous so the warm limestone architecture and Mediterranean light
            come through, while the page still reads as the institutional navy
            GMH parent. */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(31, 58, 92, 0.38)" }}
        />
        {/* Reinforced bottom gradient: protects the Portfolio card against the
            bright sky in the upper half of the image, keeps the BrandLabel and
            wordmarks legible on any device. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background:
              "linear-gradient(180deg, rgba(31,58,92,0) 0%, rgba(15,29,46,0.65) 100%)",
          }}
        />

        <div className="relative container-rail pt-14 pb-20 md:pt-24 md:pb-28">
          <BrandLabel className="text-primary-foreground/70" withRule>The Brand Portfolio · EST. 2017</BrandLabel>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <BrandHeading level={1} className="mt-6 max-w-[18ch] text-primary-foreground">Three brands. One hospitality.</BrandHeading>
            <p className="mt-6 max-w-[58ch] text-primary-foreground/80 text-[16px] md:text-[18px] leading-[1.7]">Castlewood for heritage. Vela for the coast. Brionj for Private Residences</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <BrandButton to="/search" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-transparent" data-testid="hero-find-stay">Find a stay</BrandButton>
            </div>
          </motion.div>
          <div className="mt-12 border border-primary-foreground/20 p-6 md:p-8 max-w-3xl bg-primary/40 backdrop-blur">
            <BrandLabel className="text-primary-foreground/60">The Portfolio</BrandLabel>
            {/* All three brand wordmarks are rendered in GMH's institutional
                treatment here — a single serif, single weight, single colour,
                aligned identically. This device is a list of brands the parent
                operates, not a preview of each brand's logo; the coloured /
                italic / per-brand typographic treatments are reserved for the
                dedicated brand cards lower on the page. */}
            <ul className="mt-4 space-y-3 text-[14px] md:text-[15px]">
              <li className="flex gap-4">
                <span className="font-display text-primary-foreground text-[18px] w-[7.5rem] shrink-0">Castlewood</span>
                <span className="text-primary-foreground/80">Heritage country houses across DACH</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display text-primary-foreground text-[18px] w-[7.5rem] shrink-0">Vela</span>
                <span className="text-primary-foreground/80">Baltic hotels &amp; apartments</span>
              </li>
              <li className="flex gap-4">
                <span className="font-display text-primary-foreground text-[18px] w-[7.5rem] shrink-0">Brionj</span>
                <span className="text-primary-foreground/80">Private residences. By enquiry.</span>
              </li>
            </ul>
            <Link to="/search" className="mt-6 inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.16em] underline underline-offset-4" data-testid="hero-view-portfolio">View the portfolio <ArrowUpRight size={12} /></Link>
          </div>
        </div>
      </section>

      {/* ---------------- PORTFOLIO ---------------- */}
      <section id="portfolio" className="bg-background" data-testid="gmh-portfolio">
        <div className="container-rail py-20 md:py-28">
          <div className="text-center">
            <BrandLabel withRule>The Portfolio</BrandLabel>
            <BrandHeading level={2} className="mt-4 max-w-[24ch] mx-auto">Three brands of hospitality.</BrandHeading>
            <p className="mt-4 max-w-[60ch] mx-auto text-muted-foreground">Each brand operates with its own voice, palette, and editorial register.</p>
          </div>

          <div className="mt-14 space-y-10">
            {PORTFOLIO.map((p) => (
              <motion.div key={p.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}>
                <BrandCard className="overflow-hidden grid md:grid-cols-[1.2fr_1fr]">
                  {/* Image cell is locked to a 4:3 aspect ratio on desktop so
                      all three brand cards share consistent image proportions
                      regardless of how many lines of stats each card carries.
                      Without this floor/ceiling, a card whose stats wrap onto
                      a second line (Vela's third stat used to) would silently
                      stretch its image taller than the others. */}
                  <Link to={p.href} className="block group relative aspect-[16/10] md:aspect-[4/3]">
                    <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  </Link>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <BrandLabel>{p.label}</BrandLabel>
                    <div className={`mt-3 font-display text-[36px] md:text-[44px] leading-none ${p.slug === "brionj" ? "italic text-[hsl(var(--brand-accent))]" : ""} ${p.slug === "castlewood" ? "italic" : ""}`} style={ p.slug === "castlewood" ? { fontFamily: "'Playfair Display', serif", color: "#5C2730" } : p.slug === "vela" ? { fontFamily: "'Source Serif 4', serif", color: "#1F4E5F" } : p.slug === "brionj" ? { fontFamily: "'DM Serif Display', serif", color: "#0E0E10" } : {} }>{p.italicName}</div>
                    <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed editorial-measure">{p.description}</p>
                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                      {p.stats.map((s) => <span key={s}>{s}</span>)}
                    </div>
                    <div className="mt-7">
                      <BrandButton to={p.href} variant="secondary" data-testid={`portfolio-${p.slug}`}>{p.cta} <ChevronRight size={14} className="ml-1" /></BrandButton>
                    </div>
                  </div>
                </BrandCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- APPROACH (pillars) ---------------- */}
      <section className="bg-secondary" data-testid="gmh-approach">
        <div className="container-rail py-20 md:py-24">
          <div className="text-center">
            <BrandLabel withRule>The GMH Approach</BrandLabel>
            <BrandHeading level={2} className="mt-4 max-w-[22ch] mx-auto">Three brands, run as three brands.</BrandHeading>
            <p className="mt-4 max-w-[60ch] mx-auto text-muted-foreground">We keep them different on purpose — different palettes, different voices, different ways of being welcomed.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {PILLARS.map((p) => (
              <div key={p.n} className="border-t border-foreground pt-6">
                <div className="font-display text-[42px] leading-none text-foreground/30">{p.n}</div>
                <div className="font-display text-[22px] mt-3">{p.t}</div>
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- DISPATCHES ---------------- */}
      <section className="bg-primary text-primary-foreground" data-testid="gmh-dispatches">
        <div className="container-rail py-20 md:py-24">
          <div className="text-center">
            <BrandLabel className="text-primary-foreground/70" withRule>Stories from the Portfolio</BrandLabel>
            <BrandHeading level={2} className="mt-4 max-w-[22ch] mx-auto text-primary-foreground">Dispatches from three brands.</BrandHeading>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-primary-foreground/5 border border-primary-foreground/10 p-6">
              <BrandLabel className="text-primary-foreground/60">Castlewood · Journal</BrandLabel>
              <div className="mt-4 font-display text-[22px] italic" style={{ fontFamily: "'Playfair Display', serif" }}>Twelve nights at Schlosshotel Althörnitz.</div>
              <p className="mt-3 text-[13px] text-primary-foreground/80 leading-relaxed">A guest who returns by herself each spring, and how the kitchen remembers her order before she does.</p>
              <Link to="/castlewood" className="mt-5 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-primary-foreground/80 hover:text-primary-foreground">Read the journal <ArrowUpRight size={11} /></Link>
            </div>
            <div className="bg-primary-foreground/5 border border-primary-foreground/10 p-6">
              <BrandLabel className="text-primary-foreground/60">Vela · Notes from the Via</BrandLabel>
              <div className="mt-4 font-display text-[22px]" style={{ fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>Follow that dream</div>
              <p className="mt-3 text-[13px] text-primary-foreground/80 leading-relaxed">A slower kind of magazine: the bookshelves over the bar are stocked by the wind.</p>
              <Link to="/vela" className="mt-5 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-primary-foreground/80 hover:text-primary-foreground">Read the journal <ArrowUpRight size={11} /></Link>
            </div>
            <div className="bg-primary-foreground/5 border border-primary-foreground/10 p-6">
              <BrandLabel className="text-primary-foreground/60">Brionj · The Quiet Hours</BrandLabel>
              <div className="mt-4 font-display text-[22px] italic" style={{ fontFamily: "'DM Serif Display', serif", color: "#C5B188" }}>The guests don't visit — they arrive.</div>
              <p className="mt-3 text-[13px] text-primary-foreground/80 leading-relaxed">A house manager on what hospitality means at the upper end of "by appointment."</p>
              <Link to="/brionj" className="mt-5 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[#C5B188]">Read the journal <ArrowUpRight size={11} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default GMHHome;
